import { FormEvent, useState } from "react";
import { useCreateSkaterLogin } from "../lib/queries";
import { ErrorState } from "./Loading";

/**
 * Lets a guardian give a minor their own login.
 *
 * The endpoint existed but was reachable only from the last step of the signup flow, so a guardian who
 * skipped it there could never come back and do it -- there was no route to it from anywhere in the app.
 *
 * The warning is not decoration. Creating this login flips the guardian's own access to that profile from
 * editable to read-only, live and immediately (architecture §2c) -- they can still see everything but can
 * no longer log sessions on the child's behalf. That is a surprising, one-way change to make without
 * saying so first.
 */
export function SkaterLoginForm({ clientId, clientName }: { clientId: string; clientName: string }) {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const createLogin = useCreateSkaterLogin(clientId);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!username.trim() || !password) return;
    createLogin.mutate(
      { username: username.trim(), password },
      {
        onSuccess: () => {
          setOpen(false);
          setUsername("");
          setPassword("");
        },
      }
    );
  }

  if (!open) {
    return (
      <button type="button" className="chip" onClick={() => setOpen(true)}>
        Create a login
      </button>
    );
  }

  return (
    <form className="plan-form" onSubmit={handleSubmit}>
      <p className="form-label">Create a login for {clientName}</p>
      <p className="plan-summary">
        Once {clientName} has their own login, you&rsquo;ll still see their plans and progress, but they
        will log their own sessions instead of you logging for them. This can&rsquo;t be undone.
      </p>

      {/* No email field, deliberately: a minor never has an email address on file (architecture §2c),
          which is exactly why this is username-based. */}
      <label htmlFor={`skater-username-${clientId}`}>Username</label>
      <input
        id={`skater-username-${clientId}`}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        autoComplete="off"
        required
      />

      <label htmlFor={`skater-password-${clientId}`}>Password</label>
      <input
        id={`skater-password-${clientId}`}
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        required
      />

      {createLogin.isError && <ErrorState error={createLogin.error} />}

      <button type="submit" disabled={createLogin.isPending}>
        {createLogin.isPending ? "Creating…" : "Create login"}
      </button>
      <button type="button" className="chip" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </form>
  );
}
